import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

/**
 * Handoff card for a BPMN diagram the `model` specialist produced in chat.
 *
 * The Assistant deliberately has no canvas: the specialist converses, and
 * the diagram is opened in the real modeller, which is where participants,
 * validation and persistence live. We navigate to the "new process model"
 * page and hand the XML over in `location.state` -- NewProcessModule imports
 * it once the modeller is ready. No endpoint and no existing screen changes.
 */

interface BpmnArtifactCardProps {
  xml: string;
  filename?: string;
}

const BpmnArtifactCard: React.FC<BpmnArtifactCardProps> = ({ xml, filename }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(xml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable outside a secure context; the "open in the
      // modeller" path is the primary action anyway, so stay quiet.
    }
  };

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1.5,
        my: 0.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AccountTreeOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Diagramma BPMN pronto
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {filename || "processo.bpmn"} · {xml.length.toLocaleString()} caratteri
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          sx={{ textTransform: "none" }}
          onClick={() =>
            navigate("/dashboard/process/insert", {
              state: { bpmnXml: xml, bpmnFilename: filename },
            })
          }
        >
          Apri nel modellatore
        </Button>
        <Button
          size="small"
          startIcon={<ContentCopyIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
          onClick={handleCopy}
        >
          {copied ? "Copiato" : "Copia XML"}
        </Button>
      </Stack>
    </Box>
  );
};

export default BpmnArtifactCard;
